import {
  isSameLandingUploadAttempt,
  type LandingUploadAttempt,
  type LandingUploadDimensions,
} from "./landing-upload-attempt";

export type LandingUploadFailureStage =
  | "credit_gate"
  | "existing_anonymous_job"
  | "job_create"
  | "file_upload"
  | "job_activate";

export type LandingOwnershipPersistence = "local_storage" | "claim_link";

export type LandingUploadJourneyEvent =
  | "landing_upload_submitted"
  | "landing_upload_job_created"
  | "landing_upload_ready"
  | "landing_upload_failed"
  | "landing_upload_redirected";

export type LandingUploadCapture = (
  event: LandingUploadJourneyEvent,
  properties: LandingUploadDimensions & {
    ownership_persistence?: LandingOwnershipPersistence;
    reason?: "signed_in";
    stage?: LandingUploadFailureStage | "validation";
  }
) => void;

export const captureLandingUploadValidationFailure = (
  capture: LandingUploadCapture,
  dimensions: LandingUploadDimensions
) => capture("landing_upload_failed", { ...dimensions, stage: "validation" });

export const captureLandingUploadSignedInRedirect = (
  capture: LandingUploadCapture,
  dimensions: Exclude<LandingUploadAttempt["dimensions"], { input_method: "none" }>
) => capture("landing_upload_redirected", { ...dimensions, reason: "signed_in" });

export type LandingUploadResumePhase = "job_create" | "file_upload" | "job_activate";

export type LandingUploadResumeState = {
  attempt: LandingUploadAttempt;
  emittedEvents: Set<LandingUploadJourneyEvent>;
  jobId: string;
  phase: LandingUploadResumePhase;
};

export const prepareLandingUploadResumeState = ({
  activeState,
  attempt,
  createJobId,
}: {
  activeState: LandingUploadResumeState | null;
  attempt: LandingUploadAttempt;
  createJobId: () => string;
}): LandingUploadResumeState => {
  if (activeState && isSameLandingUploadAttempt(activeState.attempt, attempt)) {
    return activeState;
  }

  return {
    attempt,
    emittedEvents: new Set(),
    jobId: createJobId(),
    phase: "job_create",
  };
};

type LandingUploadReconciliationJob = {
  id: string;
  status: string;
  user_status: string | null;
};

type LandingUploadLifecycleDependencies = {
  activateJob: (jobId: string) => Promise<void>;
  capture: LandingUploadCapture;
  createJob: (jobId: string, input: GQL_TRANSCRIPTION_JOBS_INSERT_INPUT) => Promise<void>;
  getExistingJobIds: () => string[];
  getJob: (jobId: string) => Promise<LandingUploadReconciliationJob | null>;
  isAmbiguousError: (error: unknown) => boolean;
  onAttemptStart?: (knownJobId?: string) => void;
  onJobCreated?: (jobId: string) => void;
  persistJobId: (jobId: string) => LandingOwnershipPersistence;
  uploadFile: (jobId: string, file: File) => Promise<void>;
};

export type LandingUploadLifecycleResult =
  | { status: "credit_gate" }
  | { status: "existing_anonymous_job" }
  | {
      status: "failure";
      stage: Exclude<LandingUploadFailureStage, "credit_gate" | "existing_anonymous_job">;
    }
  | {
      status: "success";
      jobId: string;
      ownershipPersistence: LandingOwnershipPersistence;
    };

const captureOnce = (
  state: LandingUploadResumeState,
  capture: LandingUploadCapture,
  event: LandingUploadJourneyEvent,
  properties: Parameters<LandingUploadCapture>[1]
) => {
  if (state.emittedEvents.has(event)) return;
  state.emittedEvents.add(event);
  capture(event, properties);
};

const reconcileAmbiguousRequest = async ({
  dependencies,
  error,
  jobId,
  isAccepted,
}: {
  dependencies: LandingUploadLifecycleDependencies;
  error: unknown;
  jobId: string;
  isAccepted: (job: LandingUploadReconciliationJob) => boolean;
}) => {
  if (!dependencies.isAmbiguousError(error)) throw error;
  const job = await dependencies.getJob(jobId);
  if (!job || !isAccepted(job)) throw error;
};

export const executeLandingUploadAttempt = async ({
  state,
  hasCredits,
  dependencies,
}: {
  state: LandingUploadResumeState;
  hasCredits: boolean;
  dependencies: LandingUploadLifecycleDependencies;
}): Promise<LandingUploadLifecycleResult> => {
  const { attempt, jobId } = state;
  const isFirstExecution = !state.emittedEvents.has("landing_upload_submitted");
  captureOnce(state, dependencies.capture, "landing_upload_submitted", attempt.dimensions);

  if (isFirstExecution && !hasCredits) {
    captureOnce(state, dependencies.capture, "landing_upload_failed", {
      ...attempt.dimensions,
      stage: "credit_gate",
    });
    return { status: "credit_gate" };
  }

  if (isFirstExecution && dependencies.getExistingJobIds().length > 0) {
    captureOnce(state, dependencies.capture, "landing_upload_failed", {
      ...attempt.dimensions,
      stage: "existing_anonymous_job",
    });
    return { status: "existing_anonymous_job" };
  }

  dependencies.onAttemptStart?.(state.phase === "job_create" ? undefined : jobId);
  let stage = state.phase;

  try {
    if (state.phase === "job_create") {
      try {
        await dependencies.createJob(jobId, attempt.transcriptionJobInput);
      } catch (error) {
        await reconcileAmbiguousRequest({
          dependencies,
          error,
          jobId,
          isAccepted: (job) => job.id === jobId,
        });
      }
      dependencies.onJobCreated?.(jobId);
      captureOnce(state, dependencies.capture, "landing_upload_job_created", attempt.dimensions);
      state.phase = attempt.file ? "file_upload" : "job_activate";
    }

    if (attempt.file && state.phase === "file_upload") {
      stage = "file_upload";
      await dependencies.uploadFile(jobId, attempt.file);
      state.phase = "job_activate";
    }

    if (attempt.file && state.phase === "job_activate") {
      stage = "job_activate";
      try {
        await dependencies.activateJob(jobId);
      } catch (error) {
        await reconcileAmbiguousRequest({
          dependencies,
          error,
          jobId,
          isAccepted: (job) => job.user_status === "UPLOADED" || job.status !== "INITIAL",
        });
      }
    }

    let ownershipPersistence: LandingOwnershipPersistence = "claim_link";
    try {
      ownershipPersistence = dependencies.persistJobId(jobId);
    } catch {
      // The already-created demo URL is the durable recovery path. A blocked
      // storage write must never turn into a duplicate-producing form retry.
    }

    captureOnce(state, dependencies.capture, "landing_upload_ready", {
      ...attempt.dimensions,
      ownership_persistence: ownershipPersistence,
    });
    return { status: "success", jobId, ownershipPersistence };
  } catch {
    captureOnce(state, dependencies.capture, "landing_upload_failed", {
      ...attempt.dimensions,
      stage,
    });
    return { status: "failure", stage };
  }
};
